import { useState, useEffect } from 'react';
import { PALETTES, applyTheme, getSavedTheme, getSavedCustomHex } from '@/lib/themeSwitcher';
import { Button } from './ui/button';
import { Palette, Check, Sparkles } from 'lucide-react';

/**
 * ThemePicker — dropdown that swaps the app-wide CSS tokens at runtime.
 * Supports the named presets + a "Custom" hex picker for on-the-fly branding.
 */
export default function ThemePicker({ compact = false }) {
  const [current, setCurrent] = useState(getSavedTheme());
  const [open, setOpen] = useState(false);
  const [customHex, setCustomHex] = useState(getSavedCustomHex());

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest?.('[data-theme-picker]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (name) => {
    applyTheme(name);
    setCurrent(name);
    if (name !== 'custom') setOpen(false);
  };

  const applyCustom = (hex) => {
    setCustomHex(hex);
    applyTheme('custom', { hex });
    setCurrent('custom');
  };

  const label = current === 'custom' ? 'Custom' : (PALETTES[current]?.label || 'Theme');
  const swatch = current === 'custom' ? customHex : PALETTES[current]?.swatch;

  return (
    <div className="relative" data-theme-picker>
      <Button
        size="sm"
        variant="outline"
        className="rounded-xl text-xs"
        onClick={() => setOpen((v) => !v)}
        data-testid="theme-picker-btn"
        title="Change color theme"
      >
        <Palette size={14} className="mr-1" />
        {compact ? '' : label}
      </Button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 min-w-[220px] rounded-xl border border-border bg-card shadow-lg p-2"
          data-testid="theme-picker-menu"
        >
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            App Theme
          </div>
          <div className="space-y-0.5">
            {Object.entries(PALETTES).map(([key, p]) => (
              <button
                key={key}
                type="button"
                onClick={() => pick(key)}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-xs text-left transition-colors ${
                  current === key ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                }`}
                data-testid={`theme-option-${key}`}
              >
                <span
                  className="w-4 h-4 rounded-full border border-border flex-shrink-0"
                  style={{ backgroundColor: p.swatch }}
                />
                <span className="flex-1 font-medium">{p.label}</span>
                {current === key && <Check size={13} className="text-primary" />}
              </button>
            ))}

            {/* Custom hex picker — native color input for max compatibility */}
            <div
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                current === 'custom' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
              }`}
              data-testid="theme-option-custom"
            >
              <label className="flex items-center gap-2.5 flex-1 cursor-pointer">
                <span
                  className="w-4 h-4 rounded-full border border-border flex-shrink-0 relative overflow-hidden"
                  style={{ backgroundColor: customHex }}
                >
                  <Sparkles size={9} className="absolute top-0.5 left-0.5 text-white opacity-70" />
                </span>
                <span className="flex-1 font-medium">Custom</span>
                <input
                  type="color"
                  value={customHex}
                  onChange={(e) => applyCustom(e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border border-border bg-transparent p-0"
                  data-testid="theme-custom-hex-input"
                  title="Pick any color"
                />
              </label>
              {current === 'custom' && <Check size={13} className="text-primary" />}
            </div>

            {current === 'custom' && (
              <div className="flex items-center gap-2 px-2 py-1">
                <span className="text-[10px] text-muted-foreground font-mono uppercase">{customHex}</span>
                <button
                  type="button"
                  onClick={() => pick('default')}
                  className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline"
                  data-testid="theme-reset-btn"
                >
                  reset
                </button>
              </div>
            )}
          </div>
          <div className="mt-1 pt-1.5 border-t border-border px-2 py-1 text-[10px] text-muted-foreground">
            Applies instantly across the whole app
          </div>
        </div>
      )}
    </div>
  );
}
